import { useMsal } from "@azure/msal-react";
import { Col, Row } from "react-bootstrap";

import { useState, useEffect } from "react";
import AutocompleteFloatLabel from "../../../components/AutocompleteFloatLabel";
import DialogMessage from "../../../components/DialogMessage";
import DialogMessageProps from "../../../model/DialogMessageProps";
import Cartao from "../../../model/dto/Cartao";
import CartaoParams from "../../../model/params/CartaoParams";
import CartaoTipo from "../../../model/dto/CartaoTipo";
import { TypeDialogMessageEnum } from "../../../model/Enums/TypeDialogMessageEnum";
import SelectOption from "../../../model/SelectOption";
import ProfessorService from "../../../service/Docente/ProfessorService";
import ButtonStyles from "../../../styles/components/ButtonStyles";
import DialogStyles from "../../../styles/components/DialogStyles";
import GenericUtils from "../../../utils/GenericUtils";
import InputCalendarStyles from "../../../styles/components/InputCalendarStyles";
import ProfessorParams from "../../../model/params/ProfessorParams";
import Professor from "../../../model/dto/Professor";
import CartoesService from "../../../service/Docente/CartoesService";
import { TipoCartaoEnum } from "../../../model/Enums/TipoCartaoEnum";
import StringUtils from "../../../utils/types/StringUtils";
import { MASK_DATE } from "../../../utils/constants";

const { BtnPrimary } = ButtonStyles;
const { StyledContainerButton } = DialogStyles;

interface IFormReplacementProps {
    docente: Professor;
    onCreate: (typeCard: TipoCartaoEnum) => void;
    refreshScheduleCard: (cartaoAgendaId: number[]) => void;
    matriculaAgenda: string;
}

const FormReplacement: React.FC<IFormReplacementProps> = ({
    docente,
    onCreate,
    refreshScheduleCard,
    matriculaAgenda,
}) => {
    const { instance } = useMsal();

    const [datePicker, setDatePicker] = useState<any>(null);

    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);
    const [professor, setProfessor] = useState(new Array<SelectOption>());
    const [professorOptions, setProfessorOptions] = useState(
        new Array<SelectOption>()
    );
    const [professorList, setProfessorList] = useState(new Array<Professor>());

    const [card, setCard] = useState(new Array<SelectOption>());
    const [cartaoOptions, setCartaoOptions] = useState(
        new Array<SelectOption>()
    );
    const [cartaoList, setCartaoList] = useState(new Array<Cartao>());
    const [cartaoId, setCartaoId] = useState<number>(0);
    const [cartaoSubs, setCartaoSubs] = useState<number>(0);

    const isFormValid = !!professor.length && !!card.length;

    const getCartoes = async (_docenteId?: number) => {
        if (!_docenteId || !datePicker) return;
        const cartaoParams = GenericUtils.impl<CartaoParams>({
            docenteSubstituicao: true,
            docenteId: _docenteId,
            dataCartao: StringUtils.formatDateString(
                datePicker?.toISOString(),
                MASK_DATE
            ),
            tipoCartaoId: docente.gerarAgenda
                ? undefined
                : [TipoCartaoEnum.HoraLetiva],
        });

        const response = await CartoesService.getCartoes(
            instance,
            cartaoParams
        );

        if (GenericUtils.isSuccessStatus(response)) {
            setCartaoList(response.data?.cartoes);
            setCartaoOptions(
                response.data?.cartoes?.map((item: Cartao) => {
                    return {
                        label: `${item.horaInicio} - ${item.horaFim}`,
                        value: item.cartaoAgendaId,
                    };
                })
            );
        } else {
            setCartaoList([]);
            setCartaoOptions([]);
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleSelectDate = async () => {
        const professorParams = GenericUtils.impl<ProfessorParams>({
            _limit: 5000,
            ativos: true,
            matricula_agenda: matriculaAgenda,
        });

        const response = await ProfessorService.getProfessores(
            instance,
            professorParams
        );

        if (GenericUtils.isSuccessStatus(response)) {
            const professorFiltrado = response.data.filter(
                (prof: Professor) => prof.docenteID !== docente.docenteID
            );
            setProfessorList(professorFiltrado);
            setProfessorOptions(
                professorFiltrado.map((item: Professor) => {
                    return {
                        label: `${item.nome} - ${item.matriculaAdp}`,
                        value: item.docenteID,
                    };
                })
            );
        } else {
            setProfessorList([]);
            setProfessorOptions([]);
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;

        const cartao = cartaoList.find(
            (item) => item.cartaoAgendaId === card[0].value
        );

        if (!cartao) return;

        cartao.origemCartaoId = Number(cartao.cartaoAgendaId);
        cartao.cartaoAgendaId = undefined;
        cartao.data = undefined;
        cartao.horaInicio = undefined;
        cartao.horaFim = undefined;
        cartao.cartaoTipoOrigem = cartao.cartaoTipo;
        cartao.cartaoTipo = GenericUtils.impl<CartaoTipo>({
            cartaoTipoId: TipoCartaoEnum.Substituicao,
        });
        cartao.docenteId = docente.docenteID;

        const response = await CartoesService.salvarCartao(instance, cartao);

        if (GenericUtils.isSuccessStatus(response)) {
            setCartaoSubs(cartao.origemCartaoId);
            setCartaoId(Number(response.headers.location.replace(/\D/g, "")));
            setDialogMessageProps({
                show: true,
                type: TypeDialogMessageEnum.Success,
                title: "Cartão salvo com sucesso",
            });
        } else {
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleCloseDialogMessage = () => {
        if (dialogMessageProps.type === TypeDialogMessageEnum.Success)
            onCreate(TipoCartaoEnum.Antecipacao);

        if (dialogMessageProps.type === TypeDialogMessageEnum.Success) {
            refreshScheduleCard([cartaoId, cartaoSubs]);
            onCreate(TipoCartaoEnum.Substituicao);
        }

        setDialogMessageProps({} as DialogMessageProps);
    };

    useEffect(() => {
        if (!professor || professor.length === 0) return;
        const selectedProfessor = professorList.find(
            (item) => item.docenteID === Number(professor[0].value)
        );
        getCartoes(selectedProfessor?.docenteID);
    }, [professor]);

    useEffect(() => {
        if (!datePicker) return;
        handleSelectDate();
    }, [datePicker]);

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={handleCloseDialogMessage}
        />
    );

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <InputCalendarStyles.StyledCalendar
                        placeholder="Data"
                        dateFormat="dd/mm/yy"
                        locale="pt-BR"
                        autoZIndex
                        touchUI
                        readOnlyInput
                        showButtonBar
                        value={datePicker}
                        onChange={(e: any) => setDatePicker(e.target?.value)}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label="Professor"
                        options={professorOptions}
                        selected={professor}
                        onChange={setProfessor}
                        labelSecondary
                        className="modal-create-card-autocomplete"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label="Hora Aula"
                        options={cartaoOptions}
                        selected={card}
                        onChange={setCard}
                        labelSecondary
                        className="modal-create-card-autocomplete"
                    />
                </Col>
            </Row>
            <StyledContainerButton className="mt-3">
                <BtnPrimary disabled={!isFormValid} onClick={handleSubmit}>
                    Criar
                </BtnPrimary>
            </StyledContainerButton>
            {dialogMessage}
        </>
    );
};

export default FormReplacement;
